import { kTelemetryEndpoint, type TelemetryProps } from "@/shared/telemetry.api";
import type { TelemetryEventArgs, TelemetryEventName } from "@/shared/telemetry-events.api";
import { authTokenSignal } from "@/client/core/services/auth-token.signal";
import { postToServer } from "@/client/utils/postToServer";
import { setEventReporter } from "@/client/utils/event-reporter";
import { getDisplayMode } from "@/client/utils/getDisplayMode";
import { getHostSurface } from "@/client/utils/getHostSurface";

// Events fire at boot, often before the first token arrives (postToServer
// deliberately skips the token wait outside iframes so app requests don't
// stall). Telemetry is fire-and-forget, so it can afford to wait.
const kFirstTokenWaitMs = 8000;

const waitForFirstToken = (): Promise<void> =>
    new Promise((resolve) => {
        if (authTokenSignal.value.accessToken !== undefined) {
            resolve();
            return;
        }
        // Both closures run asynchronously, after removeListener is assigned.
        const timer = setTimeout(() => {
            removeListener();
            resolve();
        }, kFirstTokenWaitMs);
        const removeListener = authTokenSignal.listen(() => {
            clearTimeout(timer);
            removeListener();
            resolve();
        });
    });

/** "/apps/splice/craft/" -> "splice/craft"; anything else -> "home". */
export const getTelemetryApp = (pathname: string): string => {
    const match = /^\/apps\/(.+?)\/?$/.exec(pathname);
    return match !== null ? match[1] : "home";
};

let telemetryAudience: string | undefined;

/** Called by the feature-flags signal when audience tiers resolve. */
export const setTelemetryAudience = (audience: string | undefined): void => {
    telemetryAudience = audience;
};

export const buildTelemetryEnvelope = () => ({
    app: getTelemetryApp(window.location.pathname),
    displayMode: getDisplayMode(),
    surface: getHostSurface(),
    audience: telemetryAudience,
    commit: import.meta.env.VITE_GIT_COMMIT,
});

/**
 * Fire-and-forget usage event; never throws, never blocks UX (postToServer
 * returns an Error instead of throwing and attaches the auth token).
 *
 * The name must come from the Labs event vocabulary in
 * `shared/telemetry-events.api.ts`, and so must its props. Every app measuring
 * the same moment under the same name is the whole point: it is what lets one
 * Amplitude chart compare Magic Fit to Interplay without anyone reconciling two
 * spellings of "generated".
 */
export const trackEvent = <N extends TelemetryEventName>(...args: TelemetryEventArgs<N>): void => {
    const [name, props] = args;
    sendEvent(name, props);
};

/**
 * The untyped send. `trackEvent` is the door developers use — it pairs each
 * name with the props that name requires — and this is what it opens onto.
 * Kept separate so the capability reporter can register a plain function
 * rather than a generic one.
 */
const sendEvent = (name: TelemetryEventName, props?: TelemetryProps): void => {
    const envelope = buildTelemetryEnvelope();
    void waitForFirstToken().then(() =>
        postToServer(kTelemetryEndpoint, { kind: "event", name, props, ...envelope }),
    );
};

// Hands the capability instrumentation in `postToServer` a way to send events
// without importing this module, which would make the two circular. Runs when
// this module first loads — which every app does, via `createCore`.
setEventReporter(sendEvent);

/** Resolves true on success so the feedback widget can confirm; never throws. */
export const sendFeedback = async (input: {
    rating?: number;
    comment?: string;
}): Promise<boolean> => {
    const result = await postToServer(kTelemetryEndpoint, {
        kind: "feedback",
        ...input,
        ...buildTelemetryEnvelope(),
    });
    return !(result instanceof Error);
};
